import { memo, useCallback } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Badge, Button, SlideOut, SlideOutHeader, Skeleton } from '@aimsos/ui';

import { TRAIT_EXPIRING_SOON_DAYS } from '../../constants/intelligence.js';
import { useReportTraitIssue } from '../../hooks/useReportTraitIssue.js';
import { useTrait } from '../../hooks/useTrait.js';
import { daysUntil, formatTraitDate } from '../../utils/traitDates.js';
import ItemUnavailableState from '../item-slideout/ItemUnavailableState.jsx';
import { ErrorState } from '../states/index.js';
import TraitDetailSection from './TraitDetailSection.jsx';
import TraitHistory from './TraitHistory.jsx';
import TraitUsedIn from './TraitUsedIn.jsx';
import TraitValidity from './TraitValidity.jsx';
import { iconForTraitCategory } from './traitIcons.js';

/**
 * One trait in the profile slide-out (CT-5715): what it says, where it came
 * from, how long it stays valid, where it is used, its history, and "Report an
 * issue".
 *
 * Registered for the `trait` kind of the item slide-out host, which mounts it
 * only while `?item=trait:<id>` names one — each opening starts clean. The
 * overlay, focus trap, Escape and focus return are `SlideOut`'s; the title row
 * is `SlideOutHeader`'s. A trait that no longer exists (404) or that the viewer
 * may not open (403) shows the same "unavailable" state as an unknown link.
 *
 * @param {object} props
 * @param {string} props.itemId — the trait id.
 * @param {string} props.entityType
 * @param {string} props.entityId
 * @param {string|null} [props.entityTitle]
 * @param {() => void} props.onClose
 * @param {() => void} [props.onChanged] — after a report, so How to approach refreshes.
 */
const TraitDetailSlideOut = memo(function TraitDetailSlideOut({ itemId, entityType, entityId, entityTitle, onClose, onChanged }) {
  const { t, i18n } = useTranslation('unified-profiles');
  const language = i18n?.language;

  const { trait, loading, error, notFound, accessDenied, refetch, refresh } = useTrait({
    entityType,
    entityId,
    traitId: itemId,
  });
  const { report, reporting, reported } = useReportTraitIssue({
    entityType,
    entityId,
    traitId: itemId,
    onReported: refresh,
    onChanged,
  });

  // Its OWN namespace, not the quick-filter chips': the chips are a fixed
  // product list, while `category` is the backend's taxonomy. The first
  // category shipped that is not also a chip would otherwise print its raw key
  // as the panel's title.
  const title = trait?.category
    ? t('intelligence.traits.detail.title', {
        category: t(`intelligence.traits.categories.${trait.category}`, {
          defaultValue: t('intelligence.traits.categories.unknown'),
        }),
      })
    : t('intelligence.traits.detail.fallbackTitle');

  const renderHeader = useCallback(
    (titleId) => (
      <SlideOutHeader
        titleId={titleId}
        title={title}
        icon={iconForTraitCategory(trait?.category)}
        size="M"
        showClose
        onClose={onClose}
      />
    ),
    [title, trait?.category, onClose],
  );

  const body = () => {
    if (loading) {
      return (
        <div data-testid="trait-detail-loading" className="flex flex-col gap-3" aria-busy>
          <Skeleton width="30%" height="14px" borderRadius="4px" />
          <Skeleton width="60%" height="20px" borderRadius="4px" />
          <Skeleton width="90%" height="14px" borderRadius="4px" />
        </div>
      );
    }
    if (notFound || accessDenied) return <ItemUnavailableState />;
    if (error) return <ErrorState message={error} onRetry={refetch} />;
    if (!trait) return null;

    const days = daysUntil(trait.expiresAt);
    const expiringSoon = days != null && days >= 0 && days <= TRAIT_EXPIRING_SOON_DAYS;
    const sourceDate = formatTraitDate(trait.source?.at, language);
    const sourceTime = trait.source?.at
      ? new Date(trait.source.at).toLocaleTimeString(language, { hour: 'numeric', minute: '2-digit' })
      : null;

    return (
      <div data-testid="trait-detail" data-trait-id={trait.id} className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          {trait.label ? <span className="text-xs text-content-muted">{trait.label}</span> : null}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-lg font-semibold text-content">{trait.value ?? trait.title}</span>
            {trait.sourceKind ? (
              <Badge variant="neutral" size="small">
                {t(`intelligence.traits.sourceKind.${trait.sourceKind}`)}
              </Badge>
            ) : null}
            {trait.unverified ? (
              <Badge variant="warning" size="small">
                {t('intelligence.traits.card.unverified')}
              </Badge>
            ) : null}
            {expiringSoon ? (
              <Badge variant="warning" size="small">
                {t('intelligence.traits.detail.expiringSoon')}
              </Badge>
            ) : null}
          </div>
          {trait.summary ? <p className="text-sm text-content-secondary">{trait.summary}</p> : null}
        </div>

        {trait.source ? (
          <TraitDetailSection title={t('intelligence.traits.detail.source')}>
            <div className="flex flex-col gap-0.5 text-sm">
              <span className="font-medium text-content">{trait.source.label}</span>
              <span className="text-content-secondary">
                {[trait.source.author, [sourceDate, sourceTime].filter(Boolean).join(' · ')]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </div>
          </TraitDetailSection>
        ) : null}

        <TraitDetailSection title={t('intelligence.traits.detail.validity')}>
          {trait.unverified && trait.disappearsInDays != null ? (
            <p className="text-sm text-content-secondary">
              {t('intelligence.traits.card.disappears', { count: trait.disappearsInDays })}
            </p>
          ) : (
            <TraitValidity since={trait.since} expiresAt={trait.expiresAt} />
          )}
        </TraitDetailSection>

        <TraitDetailSection title={t('intelligence.traits.detail.usedIn')}>
          <TraitUsedIn usedIn={trait.usedIn} />
          {trait.visibility ? (
            <p className="text-sm text-content-secondary">
              {t(`intelligence.traits.detail.visibility.${trait.visibility}`, {
                name: entityTitle ?? t('profile.untitled'),
              })}
            </p>
          ) : null}
        </TraitDetailSection>

        {trait.history.length ? (
          <TraitDetailSection title={t('intelligence.traits.detail.history')}>
            <TraitHistory history={trait.history} />
          </TraitDetailSection>
        ) : null}
      </div>
    );
  };

  return (
    <SlideOut isOpen onClose={onClose} renderHeader={renderHeader} size="M" ariaLabel={title}>
      <div data-testid="trait-slideout" data-item-id={itemId} className="flex h-full min-h-0 flex-col gap-4">
        <div className="scrollbar-aimsos-auto min-h-0 flex-1 overflow-y-auto">{body()}</div>

        {trait ? (
          <div className="flex shrink-0 items-center justify-between gap-3 border-t border-edge pt-4">
            <span className="text-xs text-content-muted">{t('intelligence.traits.detail.reportHint')}</span>
            <Button
              variant="secondary"
              size="sm"
              onClick={report}
              disabled={reporting || reported || trait.reportOpen}
            >
              {reported || trait.reportOpen
                ? t('intelligence.traits.detail.reported')
                : t('intelligence.traits.detail.report')}
            </Button>
          </div>
        ) : null}
      </div>
    </SlideOut>
  );
});

export default TraitDetailSlideOut;
